import React, { useState } from "react";
import { Button, Table, Dropdown } from "flowbite-react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getToken, getUserRole } from "../utils/auth";
import { 
  ClipboardCheck, 
  Users, 
  Loader2, 
  Filter, 
  Download, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Eye, 
  MoreVertical,
  DollarSign,
  GraduationCap
} from "lucide-react";

export default function LocalLoanApproveStep() {
  const [selectionSettings, setSelectionSettings] = useState([]);
  const userRole = getUserRole();

  const nav = useNavigate();

  const getSettings = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL + "hec/settings/getSettings",
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      const settings = await response.data;
      setSelectionSettings(settings);
      return selectionSettings;
    } catch (error) {
      toast.error("Failed to fetch settings", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    }
  };

  const updateStatus = async (status, settingsId) => {
    await axios
      .get(
        process.env.REACT_APP_BASE_URL +
          `hec/settings/updateSettings?settingID=${settingsId}&status=${status}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      )
      .then((response) => {
        toast.success("Setting updated to " + status + ".", {
          position: "top-center",
          autoClose: 5000,
          theme: "colored",
        });
        window.location.reload(false);
      })
      .catch((error) => {
        toast.error("Settings not updated", {
          position: "top-center",
          autoClose: 5000,
          theme: "colored",
        });
      });
  };

  const navigatingToLoan = (sections, academicYear) => {
    const categories = {
      section: sections,
      academicYear: academicYear,
    };
    sessionStorage.setItem("categories", JSON.stringify(categories));
  };

  const { isPending, error, data } = useQuery({
    queryKey: ["settingsID"],
    queryFn: () => getSettings(),
    retry: 3,
    staleTime: 10000,
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'APPROVED':
        return (
          <span className="inline-flex items-center space-x-1 px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
            <CheckCircle className="w-3 h-3" />
            <span>Approved</span>
          </span>
        );
      case 'VERIFIED':
        return (
          <span className="inline-flex items-center space-x-1 px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
            <CheckCircle className="w-3 h-3" />
            <span>Verified</span>
          </span>
        );
      case 'RETURNED':
        return (
          <span className="inline-flex items-center space-x-1 px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
            <XCircle className="w-3 h-3" />
            <span>Returned</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center space-x-1 px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
            <Clock className="w-3 h-3" />
            <span>Pending</span>
          </span>
        );
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-RW', {
      style: 'currency',
      currency: 'RWF',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>

      <div className="p-6 sm:ml-64 transition-all duration-300 flex-1 flex flex-col">
        {/* Page Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <ClipboardCheck className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                SELECTION APPROVAL LEVELS
              </h1>
              <p className="text-gray-600 mt-2">Manage loan selection settings and approval workflow</p>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gradient-to-r from-cyan-50 to-blue-50 px-4 py-3 rounded-xl border border-cyan-200">
                  <Users className="w-5 h-5 text-cyan-600" />
                  <span className="font-semibold">{selectionSettings.length} Settings</span>
                </div>
              </div>
              <div className="mt-6 sm:mt-0 flex items-center space-x-4">
                <button className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]">
                  <Download className="w-5 h-5" />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Table Content */}
          <div className="p-6">
            {isPending ? (
              <div className="flex items-center justify-center py-24">
                <div className="text-center">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-r from-cyan-200 to-blue-200 rounded-full animate-pulse mx-auto mb-4"></div>
                    <Loader2 className="w-8 h-8 text-cyan-600 animate-spin absolute top-4 left-1/2 transform -translate-x-1/2" />
                  </div>
                  <p className="text-gray-700 text-xl font-semibold mb-2">Loading Settings</p>
                  <p className="text-gray-500">Please wait while we fetch the approval settings...</p>
                </div>
              </div>
            ) : userRole === "Admin" || userRole === "DG" ? (
              <div>
                {selectionSettings.length > 0 ? (
                  <>
                    {/* Enhanced Table */}
                    <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm">
                      <Table striped className="w-full">
                        <Table.Head className="bg-gradient-to-r from-gray-50 to-cyan-50">
                          <Table.HeadCell className="text-gray-700 font-semibold">Category</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Students</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Living Allowance</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Total Living</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Tuition Fee</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Total Tuition</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Academic Year</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Status</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Actions</Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y divide-gray-200">
                          {selectionSettings.map((settings) => (
                            <Table.Row
                              className="bg-white hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 transition-all duration-200 border-b border-gray-100"
                              key={settings.id}
                            >
                              <Table.Cell>
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center shadow-md">
                                    <GraduationCap className="w-5 h-5 text-white" />
                                  </div>
                                  <div>
                                    <div className="font-semibold text-gray-900 text-sm">
                                      {settings.studentCategory}
                                    </div>
                                  </div>
                                </div>
                              </Table.Cell>
                              <Table.Cell>
                                <span className="inline-flex items-center space-x-1 px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                                  <Users className="w-3 h-3" />
                                  <span>{settings.selectedNumber}</span>
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                <span className="text-sm font-medium text-gray-900">
                                  {formatCurrency(settings.livingAllowancePerStudent)}
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                <span className="text-sm font-bold text-green-600">
                                  {formatCurrency(settings.totalLivingAllowance)}
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                <span className="text-sm font-medium text-gray-900">
                                  {formatCurrency(settings.tuitionFeePerStudent)}
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                <span className="text-sm font-bold text-green-600">
                                  {formatCurrency(settings.totalTuitionFees)}
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200">
                                  {settings["academicYear"]?.academicYearName}
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                {getStatusBadge(settings.status)}
                              </Table.Cell>
                              <Table.Cell>
                                <Dropdown
                                  label=""
                                  dismissOnClick={false}
                                  renderTrigger={() => (
                                    <button className="flex items-center justify-center w-8 h-8 text-gray-400 rounded-lg hover:bg-gray-100 hover:text-gray-600 transition-colors duration-200">
                                      <MoreVertical className="w-4 h-4" />
                                    </button>
                                  )}
                                >
                                  {userRole === "Admin" ||
                                  userRole === "DG" ? (
                                    <>
                                      <Dropdown.Item>
                                        <button
                                          className="flex items-center space-x-2 w-full text-left"
                                          onClick={() => {
                                            navigatingToLoan(
                                              settings.studentCategory,
                                              settings["academicYear"]?.academicYearName
                                            );
                                            nav("/localLoan");
                                          }}
                                        >
                                          <Eye className="w-4 h-4" />
                                          <span>View Student List</span>
                                        </button>
                                      </Dropdown.Item>
                                      {settings.status !== "APPROVED" && (
                                        <Dropdown.Item
                                          onClick={() => {
                                            updateStatus("verify", settings.id);
                                          }}
                                        >
                                          <div className="flex items-center space-x-2">
                                            <CheckCircle className="w-4 h-4 text-blue-600" />
                                            <span>Verify</span>
                                          </div>
                                        </Dropdown.Item>
                                      )}
                                    </>                                  ) : null}

                                  {userRole === "DG" &&
                                  (settings.status === "VERIFIED" || settings.status === "APPROVED") ? (
                                    <>
                                      <Dropdown.Item
                                        onClick={() =>
                                          updateStatus("approve", settings.id)
                                        }
                                      >
                                        <div className="flex items-center space-x-2">
                                          <CheckCircle className="w-4 h-4 text-green-600" />
                                          <span>Approve</span>
                                        </div>
                                      </Dropdown.Item>
                                      <Dropdown.Item
                                        onClick={() =>
                                          updateStatus("return", settings.id)
                                        }
                                      >
                                        <div className="flex items-center space-x-2">
                                          <XCircle className="w-4 h-4 text-red-600" />
                                          <span>Return</span>
                                        </div>
                                      </Dropdown.Item>
                                    </>
                                  ) : null}
                                </Dropdown>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-24">
                    <div className="w-20 h-20 bg-gradient-to-r from-gray-200 to-cyan-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      <ClipboardCheck className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-700 text-xl font-semibold mb-2">No Settings Found</p>
                    <p className="text-gray-500 max-w-md mx-auto">
                      No selection settings have been configured yet. Please create selection settings first.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-24">
                <div className="w-20 h-20 bg-gradient-to-r from-red-200 to-pink-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-red-400" />
                </div>
                <p className="text-red-600 text-xl font-semibold mb-2">Access Denied</p>
                <p className="text-gray-600">You don't have permission to view this page</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <FooterComponents />
      </div>
    </div>
  );
}
